import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PackageIcon, ShoppingCartIcon, IndianRupeeIcon, ClockIcon, AlertTriangleIcon, PlusIcon, ListIcon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useStore } from '../../context/StoreContext';
export function AdminDashboard() {
  const {
    products,
    orders
  } = useStore();
  const stats = useMemo(() => {
    const totalRevenue = orders.filter(o => o.status !== 'Cancelled').reduce((sum, o) => sum + o.totalAmount, 0);
    const pendingOrders = orders.filter(o => o.status === 'Placed' || o.status === 'Confirmed').length;
    return {
      totalProducts: products.length,
      totalOrders: orders.length,
      totalRevenue,
      pendingOrders
    };
  }, [products, orders]);
  const lowStockProducts = useMemo(() => {
    return products.filter(p => Object.values(p.sizes).some(stock => stock > 0 && stock <= 3));
  }, [products]);
  const recentOrders = orders.slice(0, 5);
  // Mock chart data (last 7 days)
  const chartData = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dayStr = date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short'
      });
      const dayOrders = orders.filter(o => {
        const orderDate = new Date(o.date);
        return orderDate.toDateString() === date.toDateString();
      });
      days.push({
        name: dayStr,
        orders: dayOrders.length || Math.floor(Math.random() * 5) + 1,
        revenue: dayOrders.reduce((s, o) => s + o.totalAmount, 0) || Math.floor(Math.random() * 5000) + 1000
      });
    }
    return days;
  }, [orders]);
  const statCards = [{
    label: 'Total Products',
    value: stats.totalProducts,
    icon: PackageIcon,
    color: 'bg-sky-500'
  }, {
    label: 'Total Orders',
    value: stats.totalOrders,
    icon: ShoppingCartIcon,
    color: 'bg-indigo-500'
  }, {
    label: 'Revenue',
    value: `₹${stats.totalRevenue.toLocaleString()}`,
    icon: IndianRupeeIcon,
    color: 'bg-green-500'
  }, {
    label: 'Pending Orders',
    value: stats.pendingOrders,
    icon: ClockIcon,
    color: 'bg-orange-500'
  }];
  const statusColors: Record<string, string> = {
    Placed: 'bg-blue-100 text-blue-700',
    Confirmed: 'bg-indigo-100 text-indigo-700',
    Shipped: 'bg-orange-100 text-orange-700',
    Delivered: 'bg-green-100 text-green-700',
    Cancelled: 'bg-red-100 text-red-700'
  };
  return <div className="space-y-6" data-id="element-413">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-414">
        {statCards.map((stat, i) => <motion.div key={stat.label} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-415">
            <div className="flex items-center justify-between mb-3" data-id="element-416">
              <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`} data-id="element-417">
                <stat.icon className="w-5 h-5 text-white" data-id="element-418" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900" data-id="element-419">{stat.value}</p>
            <p className="text-sm text-slate-500" data-id="element-420">{stat.label}</p>
          </motion.div>)}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3" data-id="element-421">
        <Link to="/admin/products" className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white text-sm font-medium rounded-lg hover:bg-sky-700 transition-colors" data-id="element-422">
          <PlusIcon className="w-4 h-4" data-id="element-423" /> Add Product
        </Link>
        <Link to="/admin/orders" className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 text-sm font-medium rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-424">
          <ListIcon className="w-4 h-4" data-id="element-425" /> View All Orders
        </Link>
      </div>

      {/* Chart */}
      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-426">
        <h2 className="font-bold text-slate-900 mb-4" data-id="element-427">
          Sales Overview (Last 7 Days)
        </h2>
        <div className="h-64" data-id="element-428">
          <ResponsiveContainer width="100%" height="100%" data-id="element-429">
            <LineChart data={chartData} data-id="element-430">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-431" />
              <XAxis dataKey="name" tick={{
              fontSize: 12
            }} stroke="#94a3b8" data-id="element-432" />
              <YAxis tick={{
              fontSize: 12
            }} stroke="#94a3b8" data-id="element-433" />
              <Tooltip contentStyle={{
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
            }} data-id="element-434" />
              <Line type="monotone" dataKey="orders" stroke="#0ea5e9" strokeWidth={2} dot={{
              fill: '#0ea5e9'
            }} data-id="element-435" />
              <Line type="monotone" dataKey="revenue" stroke="#22c55e" strokeWidth={2} dot={{
              fill: '#22c55e'
            }} data-id="element-436" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-437">
        {/* Recent Orders */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-438">
          <h2 className="font-bold text-slate-900 mb-4" data-id="element-439">Recent Orders</h2>
          {recentOrders.length > 0 ? <div className="space-y-3" data-id="element-440">
              {recentOrders.map(order => <div key={order.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-id="element-441">
                  <div data-id="element-442">
                    <p className="text-sm font-mono text-slate-700" data-id="element-443">
                      {order.id}
                    </p>
                    <p className="text-xs text-slate-400" data-id="element-444">
                      {new Date(order.date).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-445">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[order.status]}`} data-id="element-446">
                      {order.status}
                    </span>
                    <p className="text-sm font-medium text-slate-900 mt-1" data-id="element-447">
                      ₹{order.totalAmount}
                    </p>
                  </div>
                </div>)}
            </div> : <p className="text-sm text-slate-400 text-center py-8" data-id="element-448">
              No orders yet
            </p>}
        </motion.div>

        {/* Low Stock Alerts */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="bg-white rounded-xl border border-slate-200 p-6" data-id="element-449">
          <div className="flex items-center gap-2 mb-4" data-id="element-450">
            <AlertTriangleIcon className="w-5 h-5 text-orange-500" data-id="element-451" />
            <h2 className="font-bold text-slate-900" data-id="element-452">Low Stock Alerts</h2>
          </div>
          {lowStockProducts.length > 0 ? <div className="space-y-3" data-id="element-453">
              {lowStockProducts.slice(0, 5).map(product => {
            const lowSizes = Object.entries(product.sizes).filter(([_, stock]) => stock > 0 && stock <= 3).map(([size, stock]) => `${size}(${stock})`);
            return <div key={product.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0" data-id="element-454">
                    <img src={product.images[0]} alt={product.name} className="w-10 h-12 object-cover rounded bg-slate-100" data-id="element-455" />
                    <div className="flex-1 min-w-0" data-id="element-456">
                      <p className="text-sm font-medium text-slate-900 truncate" data-id="element-457">
                        {product.name}
                      </p>
                      <p className="text-xs text-orange-600" data-id="element-458">
                        Low: {lowSizes.join(', ')}
                      </p>
                    </div>
                  </div>;
          })}
            </div> : <p className="text-sm text-slate-400 text-center py-8" data-id="element-459">
              All products well stocked
            </p>}
        </motion.div>
      </div>
    </div>;
}